/**
 * @module SavePngControl
 */
import "./SavePngControl.css";
import Control from 'ol/control/Control';
import { t } from '../utils/i18n';

class SavePngControl extends Control {
  constructor(opt_options) {
    const options = opt_options || {};

    // Create the container div
    const container = document.createElement('div');
    container.className = 'nhc-map-save-png-button border-top border-gray-10 padding-y-1 padding-x-2';

    container.innerHTML = `
      <button id="export-${options.id}-png" class="usa-button" type="button">
        ${t("control_save_png_label")}
      </button>
      <a id="${options.id}-png-download" class="usa-sr-only" data-filename="${options.filename}"></a>
    `;

    super({
      element: container,
      target: options.target
    });

    this.id = options.id;
    this.label = t("control_save_png_label"); // Store the original label

    // Bind event handler for the save PNG button
    const pngButton = container.querySelector(`#export-${options.id}-png`);
    pngButton.addEventListener('click', async () => {
      const saveFile = this.getFilename();

      // Provide user feedback
      pngButton.disabled = true;
      pngButton.textContent = 'Saving...';

      try {
        await this.savePNG(saveFile);
      } catch (error) {
        console.error("Failed to save PNG:", error);
        // Optionally notify the user of the error
      } finally {
        // Always restore the button state
        pngButton.disabled = false;
        pngButton.textContent = t("control_save_png_label");
      }
    });

  }

  /**
   * Updates the language-specific elements.
   */
  updateLanguage() {
    const pngButton = document.getElementById(`export-${this.id}-png`);
    if (pngButton) {
      this.label = t("control_save_png_label");
      pngButton.textContent = this.label;
    }
  }

  /**
   * Gets the saveFile filename from the data-filename attribute. We use this
   * attribute so that the filename can be set dynamically. E.g. in the
   * rip current graphic, there are day1 and day2 versions and the control that
   * toggles between them can change this setting.
   * @returns {string} The filename to save the PNG as
   */
  getFilename() {
    const filenameElement = document.getElementById(`${this.id}-png-download`);
    const saveFile = filenameElement.getAttribute('data-filename');
    return saveFile;
  }

  /**
   * Captures the content of the webpage as a PNG image while ignoring certain
   * elements (e.g., zoom and full-screen controls). It creates a downloadable 
   * link, sets it to the PNG data, and triggers a download so the user can 
   * save the image as map.png.
   * @param {string} date 
   */
  async savePNG(saveFile) {
    // Add classes to ignore certain elements
    this.addClass('div.ol-zoom', 'html2canvas-ignore');
    this.addClass('div.ol-full-screen', 'html2canvas-ignore');
    this.addClass('div.basemapToggle', 'html2canvas-ignore');
    this.addClass('div.toggle-radio', 'html2canvas-ignore');
    this.removeClass('div.ol-attribution', 'ol-collapsed');
    this.addClass('div.ol-attribution', 'save-png');
  
    const mapCanvas = document.getElementById('map-canvas');

    // Dynamically import the library when it's needed
    const { default: html2canvas } = await import('html2canvas');
  
    html2canvas(mapCanvas, {
      ignoreElements: function (el) {
        if (typeof el.className === 'string') {
          return el.className.toLowerCase().includes("html2canvas-ignore");
        }
        else {
          return false;
        }
      }
    })
    .then((canvas) => {  // Using arrow function here to access this.id
      const imageDownload = document.getElementById(`${this.id}-png-download`);
      imageDownload.href = canvas.toDataURL("image/png");
      imageDownload.download = saveFile;
      imageDownload.click();
      this.addClass('div.ol-attribution', 'ol-collapsed');
      this.removeClass('div.ol-attribution', 'save-png');
    });
  }

  addClass(selector, className) {
    const element = document.querySelector(selector);
    if (element) {
      element.classList.add(className);
    }
  }

  removeClass(selector, className) {
    const element = document.querySelector(selector);
    if (element && element.classList.contains(className)) {
      element.classList.remove(className);
    }
  }

}

export default SavePngControl;